import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { Label, Grid, SectionHeader, PageHeader, css, Toast, MontoInput, Spinner, Badge, Modal } from '../components/UI';
import { hoyISO } from '../utils';

const BASE = process.env.REACT_APP_API_URL || '/api';

const fmt = (n) => `$${(parseFloat(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
const ACCENT = '#ff541c'; // naranja Selectshop (hex: se le concatena opacidad)

// ── Buscador de proveedores (catálogo) ──────────────────────────────────────
function ProveedorSearch({ value, onChange, onSelect }) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const wrap = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try { const d = await api.get(`/proveedores?q=${encodeURIComponent(q)}`); setResults(d.items || []); }
    catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { clearTimeout(timer.current); timer.current = setTimeout(() => search(value), 280); return () => clearTimeout(timer.current); }, [value, search]);
  useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <input value={value} placeholder="Buscar proveedor..."
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        style={{ ...css.input(false), fontSize: 12.5, padding: '8px 11px' }}
      />
      {open && (results.length > 0 || loading) && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999, background: 'rgba(255,255,255,0.82)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 10, boxShadow: '0 12px 32px rgba(15,23,42,0.10)', maxHeight: 240, overflowY: 'auto', marginTop: 4 }}>
          {loading && <div style={{ padding: '10px 14px', color: 'var(--glass)', fontSize: 13 }}>Buscando...</div>}
          {results.map((p, i) => (
            <div key={p._id || i} onMouseDown={() => { onSelect(p); setOpen(false); }}
              style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: i < results.length - 1 ? '1px solid var(--hair-soft)' : 'none' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,84,28,0.04)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{p.nombre}</div>
              {p.banco && <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 1 }}>{p.banco} · {p.clabe || '—'}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const cotVacia = () => ({ proveedor: '', beneficiario: '', banco: '', clabe: '', cuenta: '', rfc: '', moneda: 'MXN', monto: '', tiempoEntrega: '', condicionesPago: '', notas: '', ganadora: false });

// ── Nuevo comparativo ───────────────────────────────────────────────────────
function NuevoComparativo({ onCreated, editar }) {
  const { user } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [sucursalesAll, setSucursalesAll] = useState([]);
  const [centros, setCentros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    empresa: '', sucursal: '', centroCostos: '', direccion: '',
    concepto: '', motivoPago: '', moneda: 'MXN', observaciones: '',
    fechaComparativo: hoyISO(),
  });
  const [cotizaciones, setCotizaciones] = useState([cotVacia(), cotVacia()]);
  // Prellenado desde una requisición aprobada (botón "Crear comparativo" del comprador)
  const [requisicion, setRequisicion] = useState(null);

  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4000); };

  // Cada cotización arranca con las partidas de la requisición (precio en blanco)
  const cotConPartidas = (r) => ({
    ...cotVacia(),
    partidas: (r?.partidas || []).map(p => ({ ...p, precioUnitario: '', importe: 0 })),
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem('comparativoDesdeRequisicion');
      if (!raw) return;
      // Un solo uso: se consume al leerlo para que el formulario no quede
      // ligado a la última requisición en visitas posteriores.
      localStorage.removeItem('comparativoDesdeRequisicion');
      const r = JSON.parse(raw);
      setRequisicion(r);
      setForm(f => ({
        ...f, empresa: r.empresa, sucursal: r.sucursal, centroCostos: r.centroCostos,
        direccion: r.direccion || '', concepto: r.concepto || '', moneda: r.moneda || 'MXN',
      }));
      if (r.partidas?.length) setCotizaciones([cotConPartidas(r), cotConPartidas(r)]);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const quitarVinculo = () => {
    localStorage.removeItem('comparativoDesdeRequisicion');
    setRequisicion(null);
    // sin requisición ya no hay desglose: el monto vuelve a capturarse directo
    setCotizaciones(cs => cs.map(c => ({ ...c, partidas: [] })));
  };

  // ── Modo edición (desde el historial): carga un comparativo existente ──────
  const [editando, setEditando] = useState(null);
  useEffect(() => {
    if (!editar) return;
    setEditando({ id: editar._id, folio: editar.folio, requisicionFolio: editar.requisicionFolio });
    setRequisicion(null);
    setForm(f => ({
      ...f,
      empresa: editar.empresa || '', sucursal: editar.sucursal || '', centroCostos: editar.centroCostos || '',
      direccion: editar.direccion || '', concepto: editar.concepto || '', motivoPago: editar.motivoPago || '',
      moneda: editar.moneda || 'MXN', observaciones: editar.observaciones || '',
      fechaComparativo: editar.fechaComparativo || f.fechaComparativo,
    }));
    setCotizaciones((editar.cotizaciones || []).map(c => ({
      proveedor: c.proveedor || '', beneficiario: c.beneficiario || '', banco: c.banco || '', clabe: c.clabe || '',
      cuenta: c.cuenta || '', rfc: c.rfc || '', moneda: c.moneda || 'MXN', monto: String(c.monto ?? ''),
      tiempoEntrega: c.tiempoEntrega || '', condicionesPago: c.condicionesPago || '', notas: c.notas || '',
      ganadora: !!c.ganadora,
      partidas: (c.partidas || []).map(p => ({
        codigo: p.codigo, descripcion: p.descripcion, unidad: p.unidad, cantidad: p.cantidad,
        precioUnitario: String(p.precioUnitario ?? ''),
        tasaIva: p.tasaIva, // viene de la requisición y sigue hasta la orden de compra
        cantidadAsignada: p.cantidadAsignada ? String(p.cantidadAsignada) : '',
      })),
    })));
    setErrors({});
  }, [editar]);

  const cancelarEdicion = () => {
    setEditando(null);
    setForm(f => ({ ...f, empresa: '', sucursal: '', centroCostos: '', direccion: '', concepto: '', motivoPago: '', observaciones: '' }));
    setCotizaciones([cotVacia(), cotVacia()]);
    setErrors({});
  };

  useEffect(() => {
    api.get('/empresas').then(all => {
      if (user?.rol === 'solicitante' && user?.empresas?.length) {
        const propias = all.filter(e => user.empresas.includes(e.nombre));
        setEmpresas(propias);
        if (propias.length === 1) setForm(f => ({ ...f, empresa: propias[0].nombre }));
      } else setEmpresas(all);
    }).catch(() => {});
    api.get('/sucursales').then(setSucursalesAll).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!form.sucursal) { setCentros([]); return; }
    api.get(`/centros?sucursal=${encodeURIComponent(form.sucursal)}`).then(setCentros).catch(() => setCentros([]));
  }, [form.sucursal]);

  const empresaObj = empresas.find(e => e.nombre === form.empresa);
  const sucursalesFiltradas = empresaObj?.sucursales?.length
    ? sucursalesAll.filter(s => empresaObj.sucursales.includes(s.nombre))
    : sucursalesAll;

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (errors[k]) setErrors(e => ({ ...e, [k]: false })); };

  // Cotizaciones
  const updCot = (idx, campo, val) => setCotizaciones(cs => cs.map((c, i) => i === idx ? { ...c, [campo]: val } : c));
  const selProveedor = (idx, p) => setCotizaciones(cs => cs.map((c, i) => i === idx ? {
    ...c, proveedor: p.nombre, beneficiario: p.beneficiario || '', banco: p.banco || '',
    clabe: p.clabe || '', cuenta: p.cuenta || '', rfc: p.rfc || '', moneda: p.moneda || 'MXN',
  } : c));
  const addCot = () => setCotizaciones(cs => [...cs, requisicion?.partidas?.length ? cotConPartidas(requisicion) : cotVacia()]);
  const removeCot = (idx) => setCotizaciones(cs => cs.length <= 2 ? cs : cs.filter((_, i) => i !== idx));
  const marcarGanadora = (idx) => setCotizaciones(cs => cs.map((c, i) => ({ ...c, ganadora: i === idx })));
  // Adjudicación dividida: cantidad de una partida que se compra a ESTE proveedor
  const updAsignada = (idx, pIdx, val) => setCotizaciones(cs => cs.map((c, i) => i !== idx ? c : ({
    ...c,
    partidas: c.partidas.map((p, j) => j === pIdx ? { ...p, cantidadAsignada: val } : p),
  })));

  // Precio por partida de un proveedor: recalcula el importe y el monto total de esa cotización
  const updPartida = (idx, pIdx, precio) => setCotizaciones(cs => cs.map((c, i) => {
    if (i !== idx) return c;
    const partidas = c.partidas.map((p, j) => j === pIdx
      ? { ...p, precioUnitario: precio, importe: (parseFloat(precio) || 0) * (p.cantidad || 0) }
      : p);
    const monto = partidas.reduce((s, p) => s + ((parseFloat(p.precioUnitario) || 0) * (p.cantidad || 0)), 0);
    return { ...c, partidas, monto: monto > 0 ? monto.toFixed(2) : '' };
  }));

  // Montos válidos y menor precio
  const montos = cotizaciones.map(c => parseFloat(c.monto)).filter(n => !isNaN(n) && n > 0);
  const menorMonto = montos.length ? Math.min(...montos) : null;
  const ganadoraIdx = cotizaciones.findIndex(c => c.ganadora);

  const cotizacionesValidas = cotizaciones.filter(c => c.proveedor.trim() && parseFloat(c.monto) > 0);

  const validate = () => {
    const e = {};
    if (!form.empresa) e.empresa = true;
    if (!form.sucursal) e.sucursal = true;
    if (!form.centroCostos) e.centroCostos = true;
    if (!form.concepto.trim()) e.concepto = true;
    if (cotizacionesValidas.length < 2) e.cotizaciones = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => ({
    requisicionId: requisicion?.requisicionId || undefined, // liga con la requisición (el backend valida y enlaza de vuelta)
    empresa: form.empresa, sucursal: form.sucursal, centroCostos: form.centroCostos, direccion: form.direccion,
    concepto: form.concepto.trim(), motivoPago: form.motivoPago.trim() || form.concepto.trim(),
    moneda: form.moneda, observaciones: form.observaciones, fechaComparativo: form.fechaComparativo,
    cotizaciones: cotizacionesValidas.map(c => ({
      proveedor: c.proveedor.trim(), beneficiario: c.beneficiario, banco: c.banco, clabe: c.clabe,
      cuenta: c.cuenta, rfc: c.rfc, moneda: c.moneda || form.moneda, monto: parseFloat(c.monto) || 0,
      tiempoEntrega: c.tiempoEntrega, condicionesPago: c.condicionesPago, notas: c.notas, ganadora: !!c.ganadora,
      partidas: (c.partidas || []).map(p => ({
        codigo: p.codigo, descripcion: p.descripcion, unidad: p.unidad, cantidad: p.cantidad,
        precioUnitario: parseFloat(p.precioUnitario) || 0,
        importe: (parseFloat(p.precioUnitario) || 0) * (p.cantidad || 0),
        tasaIva: p.tasaIva, // viene de la requisición y sigue hasta la orden de compra
        cantidadAsignada: parseFloat(p.cantidadAsignada) || 0, // adjudicación dividida
      })),
    })),
  });

  const reset = () => {
    setForm(f => ({ ...f, concepto: '', motivoPago: '', observaciones: '' }));
    setCotizaciones([cotVacia(), cotVacia()]);
    setErrors({});
  };

  // Sube el PDF adjunto de cada cotización (en el mismo orden que cotizacionesValidas)
  const subirArchivos = async (compId) => {
    for (let i = 0; i < cotizacionesValidas.length; i++) {
      const f = cotizacionesValidas[i]._file;
      if (!f) continue;
      try { const fd = new FormData(); fd.append('archivo', f); await api.upload(`/comparativos/${compId}/cotizaciones/${i}/archivo`, fd); }
      catch (e) { toast_(`No se pudo subir el archivo de ${cotizacionesValidas[i].proveedor}: ${e.message}`, 'error'); }
    }
  };

  const guardar = async () => {
    if (!validate()) { toast_('Completa los campos requeridos (mínimo 2 cotizaciones)', 'error'); return; }
    // Adjudicación dividida: no se puede asignar más cantidad de la requerida
    const basePartidas = cotizaciones.find(c => c.partidas?.length)?.partidas || [];
    const excede = basePartidas.some((p, i) =>
      cotizaciones.reduce((s, c) => s + (parseFloat(c.partidas?.[i]?.cantidadAsignada) || 0), 0) > (p.cantidad || 0));
    if (excede) { toast_('Hay partidas con más cantidad asignada de la requerida; revisa la columna Comprar', 'error'); return; }
    setLoading(true);
    try {
      const comp = editando
        ? await api.put(`/comparativos/${editando.id}`, buildPayload())
        : await api.post('/comparativos', buildPayload());
      await subirArchivos(comp._id);
      if (editando) {
        toast_(`✅ Comparativo ${comp.folio} actualizado`);
        setEditando(null);
      } else if (requisicion) {
        toast_(`✅ Comparativo ${comp.folio} ligado a ${requisicion.requisicionFolio}. Envíala a autorización desde Historial Requisiciones.`);
        quitarVinculo();
      } else {
        toast_(`✅ Comparativo ${comp.folio} guardado`);
      }
      if (onCreated) onCreated();
      reset();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  };

  const guardarYGenerar = async () => {
    if (!validate()) { toast_('Completa los campos requeridos (mínimo 2 cotizaciones)', 'error'); return; }
    if (ganadoraIdx < 0 || !cotizaciones[ganadoraIdx]?.proveedor?.trim()) {
      toast_('Selecciona la cotización ganadora (columna ✓)', 'error'); return;
    }
    setLoading(true);
    try {
      const comp = await api.post('/comparativos', buildPayload());
      await subirArchivos(comp._id);
      const r = await api.post(`/comparativos/${comp._id}/generar-solicitud`, {});
      toast_(`✅ Comparativo ${comp.folio} → Solicitud ${r.solicitud.folio} generada`);
      if (onCreated) onCreated();
      reset();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <PageHeader
        icon="📊"
        title="Comparativo de Cotizaciones"
        subtitle="Captura cotizaciones, elige al ganador y genera la solicitud de pago"
        actions={
          <>
            <button onClick={guardar} disabled={loading}
              style={{ ...css.btnGhost, border: '1px solid var(--hair)', fontSize: 12 }}>
              {loading ? '...' : '💾 Solo guardar'}
            </button>
            {/* Con requisición ligada o en edición, la solicitud se genera por su propio flujo */}
            {!requisicion && !editando && (
              <button onClick={guardarYGenerar} disabled={loading}
                style={{ ...css.btn(ACCENT), fontSize: 12, minWidth: 180 }}>
                {loading ? 'Procesando...' : '🏆 Guardar y generar solicitud'}
              </button>
            )}
          </>
        }
      />

      {/* Aviso de modo edición */}
      {editando && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
          padding: '10px 16px', borderRadius: 10, background: 'rgba(59,130,246,0.08)', border: '1.5px solid rgba(59,130,246,0.30)',
        }}>
          <span style={{ fontSize: 18 }}>✏️</span>
          <div style={{ flex: 1, fontSize: 12.5, color: '#2563eb' }}>
            Estás <b>editando el comparativo {editando.folio}</b>
            {editando.requisicionFolio && <> (ligado a la requisición <b>{editando.requisicionFolio}</b>)</>}.
            Al guardar se actualizará el existente — no se crea uno nuevo.
          </div>
          <button onClick={cancelarEdicion} title="Salir sin guardar cambios"
            style={{ padding: '4px 10px', borderRadius: 7, border: '1px solid rgba(59,130,246,0.30)', background: 'transparent', color: '#2563eb', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
            Cancelar edición
          </button>
        </div>
      )}

      {/* Aviso de vínculo con la requisición */}
      {requisicion && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
          padding: '10px 16px', borderRadius: 10, background: 'rgba(255,84,28,0.06)', border: '1.5px solid rgba(255,84,28,0.30)',
        }}>
          <span style={{ fontSize: 18 }}>📎</span>
          <div style={{ flex: 1, fontSize: 12.5, color: '#d63500' }}>
            Este cuadro quedará <b>ligado a la requisición {requisicion.requisicionFolio}</b>.
            Cada proveedor trae el <b>desglose de las partidas</b>: captura el precio unitario de cada una
            (el monto total se suma solo), marca con ✓ al ganador y presiona <b>"Solo guardar"</b>;
            después envíala a autorización desde <b>Historial Requisiciones</b>.
          </div>
          <button onClick={quitarVinculo} title="Quitar vínculo con la requisición"
            style={{ padding: '4px 10px', borderRadius: 7, border: '1px solid rgba(255,84,28,0.30)', background: 'var(--panel)', color: '#d63500', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
            Quitar vínculo
          </button>
        </div>
      )}

      {/* Datos generales */}
      <div style={{ ...css.card, padding: 24, marginBottom: 18 }}>
        <SectionHeader accent={ACCENT}>Datos Generales</SectionHeader>
        <Grid cols={2} gap={14} style={{ marginBottom: 14 }}>
          <div>
            <Label required>Compañía</Label>
            <select value={form.empresa} onChange={e => { set('empresa', e.target.value); set('sucursal', ''); set('centroCostos', ''); }} style={css.select(errors.empresa)}>
              <option value="">Seleccionar empresa...</option>
              {empresas.map(e => <option key={e._id} value={e.nombre}>{e.nombre}</option>)}
            </select>
          </div>
          <div>
            <Label>Fecha</Label>
            <input type="date" value={form.fechaComparativo} onChange={e => set('fechaComparativo', e.target.value)} style={css.input(false)} />
          </div>
        </Grid>
        <Grid cols={3} gap={14} style={{ marginBottom: 14 }}>
          <div>
            <Label required>Sucursal</Label>
            <select value={form.sucursal} onChange={e => { set('sucursal', e.target.value); set('centroCostos', ''); }} disabled={!form.empresa} style={css.select(errors.sucursal)}>
              <option value="">Seleccionar...</option>
              {sucursalesFiltradas.map(s => <option key={s._id} value={s.nombre}>{s.nombre}</option>)}
            </select>
          </div>
          <div>
            <Label required>Centro de Costos</Label>
            <select value={form.centroCostos} onChange={e => set('centroCostos', e.target.value)} disabled={!form.sucursal} style={css.select(errors.centroCostos)}>
              <option value="">Seleccionar...</option>
              {centros.map(c => <option key={c._id} value={c.nombre}>{c.nombre}</option>)}
            </select>
          </div>
          <div>
            <Label>Moneda</Label>
            <select value={form.moneda} onChange={e => set('moneda', e.target.value)} style={css.select(false)}>
              <option value="MXN">MXN</option><option value="USD">USD</option><option value="EUR">EUR</option>
            </select>
          </div>
        </Grid>
        <Grid cols={2} gap={14}>
          <div>
            <Label required>Concepto / Qué se cotiza</Label>
            <input value={form.concepto} onChange={e => set('concepto', e.target.value)} placeholder="Ej. Compra de 10 laptops Dell" style={css.input(errors.concepto)} />
            {errors.concepto && <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 3 }}>Campo requerido</div>}
          </div>
          <div>
            <Label>Motivo de Pago (para la solicitud)</Label>
            <input value={form.motivoPago} onChange={e => set('motivoPago', e.target.value)} placeholder="Si se deja vacío usa el concepto" style={css.input(false)} />
          </div>
        </Grid>
      </div>

      {/* Cuadro comparativo */}
      <div style={{ ...css.card, padding: 0, overflow: 'hidden', marginBottom: 18 }}>
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--hair)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>Cuadro Comparativo · {cotizaciones.length} cotizaciones</span>
          <button onClick={addCot} style={{ ...css.btn('#44475a', true), fontSize: 12 }}>+ Agregar cotización</button>
        </div>
        {errors.cotizaciones && <div style={{ padding: '8px 20px', color: 'var(--danger)', fontSize: 12, background: 'rgba(239,68,68,0.06)' }}>Captura al menos 2 cotizaciones con proveedor y monto.</div>}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 900 }}>
            <thead>
              <tr style={{ background: 'var(--panel)', borderBottom: '1px solid var(--hair)' }}>
                {['✓ Ganador', 'Proveedor', 'Monto', 'Tiempo entrega', 'Condiciones de pago', 'Notas', ''].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cotizaciones.map((c, idx) => {
                const monto = parseFloat(c.monto);
                const esMenor = menorMonto !== null && monto === menorMonto && monto > 0;
                const esGanadora = c.ganadora;
                return (
                  <React.Fragment key={idx}>
                  <tr style={{
                    borderBottom: c.partidas?.length ? 'none' : '1px solid var(--hair-soft)',
                    background: esGanadora ? 'rgba(255,84,28,0.06)' : 'transparent',
                  }}>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      <input type="radio" name="ganadora" checked={!!c.ganadora} onChange={() => marcarGanadora(idx)}
                        style={{ width: 18, height: 18, cursor: 'pointer', accentColor: ACCENT }} title="Marcar como ganadora" />
                    </td>
                    <td style={{ padding: '8px 12px', minWidth: 200 }}>
                      <ProveedorSearch value={c.proveedor} onChange={v => updCot(idx, 'proveedor', v)} onSelect={p => selProveedor(idx, p)} />
                      {c.banco && <div style={{ fontSize: 10.5, color: 'var(--glass)', marginTop: 2 }}>{c.banco} · {c.clabe || 's/clabe'}</div>}
                      <label style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, cursor: 'pointer',
                        fontSize: 10.5, fontWeight: 600, color: c._file ? '#15803d' : '#0369a1',
                        background: c._file ? 'rgba(22,163,74,0.08)' : 'rgba(59,130,246,0.08)', border: `1px solid ${c._file ? 'rgba(22,163,74,0.30)' : 'rgba(59,130,246,0.30)'}`,
                        borderRadius: 6, padding: '3px 8px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }} title={c._file ? c._file.name : 'Adjuntar la cotización (PDF/imagen)'}>
                        📎 {c._file ? c._file.name : 'Adjuntar cotización'}
                        <input type="file" accept=".pdf,.xml,.jpg,.jpeg,.png,.webp" style={{ display: 'none' }}
                          onChange={e => updCot(idx, '_file', e.target.files[0] || null)} />
                      </label>
                    </td>
                    <td style={{ padding: '8px 12px', minWidth: 130 }}>
                      {c.partidas?.length ? (
                        <div title="Suma automática del desglose por partida">
                          <div style={{ padding: '8px 11px', borderRadius: 8, background: 'var(--hair-soft)', border: '1.5px dashed var(--hair)', fontFamily: 'Geist Mono, monospace', fontWeight: 700, fontSize: 13, color: monto > 0 ? 'var(--ink)' : 'var(--glass)' }}>
                            {monto > 0 ? fmt(monto) : 'Σ partidas'}
                          </div>
                          {esMenor && <div style={{ fontSize: 10, color: 'var(--ok)', fontWeight: 700, marginTop: 2 }}>▼ menor precio</div>}
                        </div>
                      ) : (
                        <>
                          <MontoInput value={c.monto} onChange={v => updCot(idx, 'monto', v)} />
                          {esMenor && <div style={{ fontSize: 10, color: 'var(--ok)', fontWeight: 700, marginTop: 2 }}>▼ menor precio</div>}
                        </>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px', minWidth: 120 }}>
                      <input value={c.tiempoEntrega} onChange={e => updCot(idx, 'tiempoEntrega', e.target.value)} placeholder="Ej. 5 días" style={{ ...css.input(false), fontSize: 12.5, padding: '8px 11px' }} />
                    </td>
                    <td style={{ padding: '8px 12px', minWidth: 150 }}>
                      <input value={c.condicionesPago} onChange={e => updCot(idx, 'condicionesPago', e.target.value)} placeholder="Ej. Contado / 30 días" style={{ ...css.input(false), fontSize: 12.5, padding: '8px 11px' }} />
                    </td>
                    <td style={{ padding: '8px 12px', minWidth: 150 }}>
                      <input value={c.notas} onChange={e => updCot(idx, 'notas', e.target.value)} placeholder="Observaciones" style={{ ...css.input(false), fontSize: 12.5, padding: '8px 11px' }} />
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <button onClick={() => removeCot(idx)} disabled={cotizaciones.length <= 2} title="Quitar"
                        style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid rgba(239,68,68,0.30)', background: 'rgba(239,68,68,0.06)', color: 'var(--danger)', cursor: cotizaciones.length <= 2 ? 'not-allowed' : 'pointer', opacity: cotizaciones.length <= 2 ? 0.4 : 1, fontSize: 14 }}>🗑</button>
                    </td>
                  </tr>
                  {/* Desglose de partidas de la requisición: el comprador captura el precio por partida */}
                  {c.partidas?.length > 0 && (
                    <tr style={{ borderBottom: '1px solid var(--hair)', background: esGanadora ? 'rgba(255,84,28,0.04)' : 'var(--panel)' }}>
                      <td style={{ padding: 0 }} />
                      <td colSpan={6} style={{ padding: '0 12px 12px' }}>
                        <div style={{ border: '1px solid var(--hair)', borderRadius: 10, overflow: 'hidden', background: 'var(--panel)' }}>
                          <div style={{ padding: '5px 12px', background: 'var(--panel)', fontSize: 9.5, fontWeight: 800, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', justifyContent: 'space-between' }}>
                            <span>Desglose por partida · {c.proveedor || 'proveedor'}</span>
                            <span style={{ color: 'var(--plasma)', fontWeight: 700 }}>COMPRAR: llénalo sólo si repartes la compra entre proveedores</span>
                          </div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <tbody>
                              {c.partidas.map((p, pIdx) => (
                                <tr key={pIdx} style={{ borderBottom: pIdx < c.partidas.length - 1 ? '1px solid var(--hair-soft)' : 'none' }}>
                                  <td style={{ padding: '5px 12px', width: 90, whiteSpace: 'nowrap', color: 'var(--ink-soft)', fontWeight: 600 }}>{p.cantidad} {p.unidad || ''}</td>
                                  <td style={{ padding: '5px 12px', color: 'var(--ink)' }}>
                                    {p.codigo && <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 10.5, color: 'var(--glass)' }}>[{p.codigo}] </span>}
                                    {p.descripcion}
                                  </td>
                                  <td style={{ padding: '5px 12px', width: 140 }}>
                                    <MontoInput value={p.precioUnitario} onChange={v => updPartida(idx, pIdx, v)} placeholder="P. unitario" style={{ fontSize: 12, padding: '6px 10px' }} />
                                  </td>
                                  <td style={{ padding: '5px 12px', width: 120, textAlign: 'right', fontFamily: 'Geist Mono, monospace', fontWeight: 700, color: (parseFloat(p.precioUnitario) || 0) > 0 ? 'var(--ink)' : 'var(--glass)', whiteSpace: 'nowrap' }}>
                                    {fmt((parseFloat(p.precioUnitario) || 0) * (p.cantidad || 0))}
                                  </td>
                                  <td style={{ padding: '5px 12px', width: 92 }}>
                                    <input type="number" min="0" max={p.cantidad} value={p.cantidadAsignada ?? ''}
                                      onChange={e => updAsignada(idx, pIdx, e.target.value)}
                                      placeholder={`Comprar/${p.cantidad}`}
                                      title={`Cantidad a comprar a ${c.proveedor || 'este proveedor'} (máx. ${p.cantidad})`}
                                      style={{ ...css.input((parseFloat(p.cantidadAsignada) || 0) > (p.cantidad || 0)), fontSize: 12, padding: '6px 8px' }} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {(() => {
                            const adj = c.partidas.reduce((s, p) => s + (parseFloat(p.cantidadAsignada) || 0) * (parseFloat(p.precioUnitario) || 0), 0);
                            return adj > 0 ? (
                              <div style={{ padding: '5px 12px', borderTop: '1px solid var(--hair-soft)', fontSize: 11.5, color: 'var(--ok)', fontWeight: 700, textAlign: 'right', fontFamily: 'Geist Mono, monospace' }}>
                                Adjudicado a este proveedor: {fmt(adj)}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Cobertura de la adjudicación dividida: cuánto de cada partida quedó asignado */}
        {(() => {
          const base = cotizaciones.find(c => c.partidas?.length)?.partidas || [];
          const hayAsig = cotizaciones.some(c => (c.partidas || []).some(p => (parseFloat(p.cantidadAsignada) || 0) > 0));
          if (!hayAsig || !base.length) return null;
          const resumen = base.map((p, i) => ({
            desc: p.descripcion || `Partida ${i + 1}`,
            req: p.cantidad || 0,
            asig: cotizaciones.reduce((s, c) => s + (parseFloat(c.partidas?.[i]?.cantidadAsignada) || 0), 0),
          }));
          const faltan = resumen.filter(r => r.asig < r.req);
          const exceden = resumen.filter(r => r.asig > r.req);
          return (
            <div style={{ padding: '10px 20px', borderTop: '1px solid var(--hair)', background: 'rgba(255,84,28,0.04)' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--plasma)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                🧩 Adjudicación dividida — cobertura por partida
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {resumen.map((r, i) => (
                  <span key={i} style={{
                    fontSize: 11, padding: '3px 10px', borderRadius: 99, fontWeight: 600,
                    background: r.asig === r.req ? 'rgba(34,197,94,0.12)' : r.asig > r.req ? 'rgba(239,68,68,0.13)' : 'rgba(245,158,11,0.12)',
                    color: r.asig === r.req ? '#15803d' : r.asig > r.req ? '#be123c' : '#b45309',
                    border: '1px solid var(--hair)',
                  }}>
                    {r.desc.slice(0, 30)}: {r.asig}/{r.req}{r.asig === r.req ? ' ✓' : r.asig > r.req ? ' ¡excede!' : ` · faltan ${r.req - r.asig}`}
                  </span>
                ))}
              </div>
              {faltan.length > 0 && <div style={{ fontSize: 10.5, color: '#b45309', marginTop: 6 }}>⚠ Hay cantidades sin cubrir: se permite guardar, pero quedará compra pendiente.</div>}
              {exceden.length > 0 && <div style={{ fontSize: 10.5, color: 'var(--danger)', marginTop: 4 }}>Corrige las partidas que exceden lo requerido (no se puede guardar así).</div>}
              <div style={{ fontSize: 10.5, color: 'var(--glass)', marginTop: 4 }}>
                Se generará una solicitud de pago por cada proveedor con cantidades asignadas; en este modo el ✓ ganador es opcional.
              </div>
            </div>
          );
        })()}

        {ganadoraIdx >= 0 && cotizaciones[ganadoraIdx]?.proveedor && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid var(--hair)', background: 'rgba(255,84,28,0.06)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>🏆</span>
            <span style={{ fontSize: 13, color: '#d63500' }}>
              Ganador: <strong>{cotizaciones[ganadoraIdx].proveedor}</strong> · {fmt(cotizaciones[ganadoraIdx].monto)} {cotizaciones[ganadoraIdx].moneda}
            </span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button onClick={guardar} disabled={loading} style={{ ...css.btn('#44475a', true), fontSize: 13 }}>{loading ? '...' : '💾 Solo guardar'}</button>
        <button onClick={guardarYGenerar} disabled={loading} style={{ ...css.btn(ACCENT), fontSize: 13, minWidth: 200 }}>{loading ? 'Procesando...' : '🏆 Guardar y generar solicitud'}</button>
      </div>
    </div>
  );
}

// ── Historial de comparativos ───────────────────────────────────────────────
function HistorialComparativos({ refreshKey, onEditar }) {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [detalle, setDetalle] = useState(null);
  // Editable solo si aún no genera solicitud de pago y eres el dueño (o admin)
  const puedeEditar = (c) => !c.solicitudFolio &&
    (user?.rol === 'admin' || String(c.usuario?._id || c.usuario) === String(user?._id));
  const load = useCallback(() => {
    setLoading(true);
    api.get('/comparativos?limit=100').then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const descargarPDF = async (id, folio) => {
    try {
      const blob = await api.downloadPdf(`/comparativos/${id}/pdf`);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: `Comparativo_${folio}.pdf` }).click();
      URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
  };
  const verArchivo = (id, idx) => {
    const token = localStorage.getItem('token');
    window.open(`${BASE}/comparativos/${id}/cotizaciones/${idx}/archivo/preview?token=${token}`, '_blank');
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <PageHeader icon="🗂️" title="Historial de Comparativos" subtitle={`${data.items?.length || 0} comparativos`} />
      <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
        {loading ? <Spinner /> : !data.items?.length ? (
          <div style={{ padding: '52px 32px', textAlign: 'center', color: 'var(--glass)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-soft)' }}>Sin comparativos</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--panel)', borderBottom: '1px solid var(--hair)' }}>
                  {['Folio', 'Concepto', 'Compañía', 'Cotizaciones', 'Ganador', 'Monto', 'Solicitud', 'Fecha', 'Acciones'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map(c => {
                  const gan = (c.cotizaciones || []).find(x => x.ganadora);
                  return (
                    <tr key={c._id} style={{ borderBottom: '1px solid var(--hair-soft)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,84,28,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '10px 14px', fontFamily: 'Geist Mono, monospace', fontSize: 11.5, color: 'var(--plasma)', fontWeight: 700, whiteSpace: 'nowrap' }}>{c.folio}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--ink)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.concepto}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--ink-soft)', whiteSpace: 'nowrap' }}>{c.empresa}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--glass)' }}>{c.cotizaciones?.length || 0}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--ink)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{gan?.proveedor || <span style={{ color: 'var(--glass)' }}>—</span>}</td>
                      <td style={{ padding: '10px 14px', fontFamily: 'Geist Mono, monospace', fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap' }}>{gan ? fmt(gan.monto) : '—'}</td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        {c.solicitudFolio
                          ? <Badge color="#15803d">{c.solicitudFolio}</Badge>
                          : <Badge color="#f59e0b">Sin generar</Badge>}
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--glass)', whiteSpace: 'nowrap' }}>{new Date(c.createdAt).toLocaleDateString('es-MX')}</td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button onClick={() => descargarPDF(c._id, c.folio)} title="Descargar PDF del comparativo" style={{ padding: '4px 10px', borderRadius: 7, border: '1px solid rgba(59,130,246,0.30)', background: 'rgba(59,130,246,0.08)', cursor: 'pointer', fontSize: 12, color: 'var(--st-por-pagar)', fontWeight: 600 }}>📄 PDF</button>
                          <button onClick={() => setDetalle(c)} title="Ver cotizaciones" style={{ padding: '4px 10px', borderRadius: 7, border: '1px solid var(--hair)', background: 'var(--panel)', cursor: 'pointer', fontSize: 12 }}>👁</button>
                          {puedeEditar(c) && onEditar && (
                            <button onClick={() => onEditar(c)} title="Editar este comparativo (aún sin solicitud de pago)"
                              style={{ padding: '4px 10px', borderRadius: 7, border: '1px solid rgba(255,84,28,0.30)', background: 'rgba(255,84,28,0.06)', cursor: 'pointer', fontSize: 12, color: '#d63500', fontWeight: 600 }}>✏️ Editar</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detalle: cotizaciones del comparativo */}
      <Modal open={!!detalle} onClose={() => setDetalle(null)} title={detalle ? `Comparativo ${detalle.folio}` : ''} width={640}>
        {detalle && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13 }}>
            <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}><strong>{detalle.concepto}</strong> · {detalle.empresa}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(detalle.cotizaciones || []).map((cot, idx) => (
                <div key={idx} style={{
                  border: `1px solid ${cot.ganadora ? 'rgba(255,84,28,0.30)' : 'var(--hair)'}`, background: cot.ganadora ? 'rgba(255,84,28,0.06)' : 'var(--panel)',
                  borderRadius: 10, padding: '10px 12px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: 'var(--ink)' }}>
                        {cot.ganadora && <span title="Ganador">🏆 </span>}{cot.proveedor}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 2 }}>
                        {[cot.tiempoEntrega && `Entrega: ${cot.tiempoEntrega}`, cot.condicionesPago && `Cond.: ${cot.condicionesPago}`, cot.notas].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontWeight: 700, color: 'var(--ink)', fontFamily: 'Geist Mono, monospace' }}>{fmt(cot.monto)}</div>
                      {cot.archivo && cot.archivo.nombre
                        ? <button onClick={() => verArchivo(detalle._id, idx)} style={{ marginTop: 4, padding: '3px 9px', borderRadius: 6, border: '1px solid rgba(22,163,74,0.30)', background: 'rgba(22,163,74,0.08)', color: 'var(--ok)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>📎 Ver cotización</button>
                        : <div style={{ fontSize: 10.5, color: 'var(--glass)', marginTop: 4 }}>sin archivo</div>}
                    </div>
                  </div>
                  {/* Desglose por partida (comparativos ligados a una requisición) */}
                  {cot.partidas?.length > 0 && (
                    <div style={{ marginTop: 8, borderTop: '1px dashed var(--hair)', paddingTop: 6 }}>
                      {cot.partidas.map((p, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 11.5, color: 'var(--ink-soft)', padding: '2px 0' }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.cantidad} {p.unidad || ''} · {p.descripcion}</span>
                          <span style={{ fontFamily: 'Geist Mono, monospace', flexShrink: 0 }}>{fmt(p.precioUnitario)} → <b>{fmt(p.importe)}</b></span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button onClick={() => descargarPDF(detalle._id, detalle.folio)} style={{ ...css.btn('#0369a1') }}>📄 Descargar cuadro comparativo (PDF)</button>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default function Comparativos() {
  const [tab, setTab] = useState('nuevo');
  const [refreshKey, setRefreshKey] = useState(0);
  // Comparativo que se va a editar desde el historial (solo si aún no generó solicitud)
  const [editarComp, setEditarComp] = useState(null);

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 18, background: 'var(--hair-soft)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[['nuevo', '➕ Nuevo comparativo'], ['historial', '🗂️ Historial']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            padding: '7px 16px', borderRadius: 7, border: 'none', fontSize: 12.5, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s',
            background: tab === id ? 'var(--panel)' : 'transparent',
            color: tab === id ? 'var(--plasma)' : 'var(--glass)',
            boxShadow: tab === id ? '0 2px 8px rgba(255,84,28,0.12), inset 0 1px 0 rgba(255,255,255,0.70)' : 'none',
          }}>{label}</button>
        ))}
      </div>

      {tab === 'nuevo' && <NuevoComparativo onCreated={() => { setRefreshKey(k => k + 1); setEditarComp(null); }} editar={editarComp} />}
      {tab === 'historial' && <HistorialComparativos refreshKey={refreshKey} onEditar={c => { setEditarComp(c); setTab('nuevo'); }} />}
    </div>
  );
}
